import React, { HTMLAttributes } from 'react';
import Flex from '@components/Flex';
import Avatar from '@components/Avatar';
import DevText from '@components/Text';
import { Button, Card, Colors, Intent, Tag } from '@blueprintjs/core';
import { Endpoints } from '@app/data/consts';
import moment from 'moment';
import ConfirmationPopover from '@components/ConfirmationPopover';
import RouterLink from '@app/components/RouterLink';
import { useNavigate } from 'react-router-dom';

export interface UserSpaceParticipationListItemProps {
    isOwner: boolean;
    isSuspended: boolean;
    isRemoving?: boolean;
    userId: ID;
    addedAt: string;
    userImageSrc: string | null;
    userFullName: string;
    onRemove?: () => void;
}

export type Props = UserSpaceParticipationListItemProps & HTMLAttributes<HTMLDivElement>;

const UserSpaceParticipationListItem: React.FC<Props> = ({
    isOwner,
    isSuspended,
    isRemoving = false,
    userId,
    addedAt,
    userImageSrc,
    userFullName,
    onRemove,
    ...props
}) => {
    const navigate = useNavigate();
    const momentAddedAt = moment(addedAt);

    const renderUserSpaceName = () => {
        let content: JSX.Element | string = userFullName;

        if (!isOwner) {
            content = (
                <RouterLink
                    color={Colors.WHITE}
                    to={Endpoints.CLIENT_TEAMS.replace(':clientId', userId)}
                    lineHeight={1}
                >
                    {userFullName}
                </RouterLink>
            );
        }

        return <DevText className="mb-small">{content}</DevText>;
    };

    const renderRoleInfo = () => {
        if (isOwner) {
            return <DevText muted>Owner</DevText>;
        }

        return (
            <DevText muted>
                {isSuspended && <Tag minimal intent={Intent.DANGER} className="mr-1">Suspended</Tag>}
                Added: {momentAddedAt.format('D MMMM YYYY')} ({momentAddedAt.fromNow()})
            </DevText>
        );
    };

    const renderViewProfileButton = () => {
        if (isOwner) {
            return null;
        }

        return (
            <Button className="ml-1" onClick={() => navigate(Endpoints.CLIENT_TEAMS.replace(':clientId', userId))}>
                View profile
            </Button>
        );
    };

    const renderRemoveButton = () => {
        if (!onRemove) {
            return null;
        }

        return (
            <ConfirmationPopover
                title="Remove from team?"
                description="After confirming, this client will lose access to team resources. Proceed?"
                actions={[
                    <Button loading={isRemoving} icon="cross" intent={Intent.DANGER} onClick={onRemove}>
                        Yes, remove
                    </Button>,
                ]}
            >
                <Button minimal loading={isRemoving} className="ml-1" intent={Intent.DANGER}>
                    Remove
                </Button>
            </ConfirmationPopover>
        );
    };

    return (
        <Card compact {...props}>
            <Flex align="center" justify="space-between">
                <Flex direction="row" align="center">
                    <Avatar alt={userFullName} src={userImageSrc} className="mr-1" />
                    <div>
                        {renderUserSpaceName()}
                        {renderRoleInfo()}
                    </div>
                </Flex>

                <Flex direction="row" align="center">
                    {renderViewProfileButton()}
                    {renderRemoveButton()}
                </Flex>
            </Flex>
        </Card>
    );
};

export default UserSpaceParticipationListItem;
